import { createElement } from "react";
import { createRoot, type Root } from "react-dom/client";

import { SkinnyWidgetApp } from "./SkinnyWidgetApp";
import {
  applyResponsivePositionToConfig,
  parseWidgetConfigFromDataset,
  widgetConfigSchema,
  type WidgetConfig,
} from "./widget-config";
import { fetchWidgetTheme } from "@/lib/widget/fetch-widget-theme";
import { mergeEmbedWithRemoteTheme } from "@/lib/widget/widget-theme-merge";
import { resolveMountTarget } from "@/lib/widget/target-id";
import { WIDGET_SHADOW_CSS } from "widget-shadow-css";

/**
 * Capture the bootstrap `<script>` on the first synchronous tick of this bundle.
 * If we defer to `DOMContentLoaded`, `document.currentScript` is already null there.
 */
const bootstrapScriptTag: HTMLScriptElement | null =
  typeof document !== "undefined" && document.currentScript instanceof HTMLScriptElement
    ? document.currentScript
    : null;

const CUSTOM_ELEMENT_TAG = "skinny-widget";
const WIDGET_CONFIG_ATTR = "data-s2r-widget-config";

class SkinnyWidgetElement extends HTMLElement {
  private reactRoot: Root | null = null;

  connectedCallback(): void {
    if (this.reactRoot) {
      return;
    }

    const raw = this.getAttribute(WIDGET_CONFIG_ATTR);
    if (!raw) {
      console.error(
        "[Scale2Rev Engage Widget] Missing configuration attribute on host element.",
      );
      return;
    }

    let config: WidgetConfig;
    try {
      const parsedJson: unknown = JSON.parse(raw);
      config = widgetConfigSchema.parse(parsedJson);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.error(`[Scale2Rev Engage Widget] Invalid configuration: ${message}`);
      return;
    }

    const shadow = this.attachShadow({ mode: "open" });

    const styleEl = document.createElement("style");
    styleEl.setAttribute("data-s2r-component", "widget-shadow-tailwind");
    styleEl.textContent = WIDGET_SHADOW_CSS;
    shadow.appendChild(styleEl);

    const mount = document.createElement("div");
    mount.setAttribute("data-s2r-component", "public-widget-root");
    shadow.appendChild(mount);

    this.reactRoot = createRoot(mount);
    this.reactRoot.render(createElement(SkinnyWidgetApp, { config }));
  }

  disconnectedCallback(): void {
    if (this.reactRoot) {
      this.reactRoot.unmount();
      this.reactRoot = null;
    }
  }
}

function getBootstrapScript(): HTMLScriptElement | null {
  if (bootstrapScriptTag instanceof HTMLScriptElement && bootstrapScriptTag.isConnected) {
    return bootstrapScriptTag;
  }
  if (typeof document === "undefined") {
    return null;
  }
  const candidates = document.querySelectorAll('script[src*="widget.js"]');
  const last = candidates[candidates.length - 1];
  return last instanceof HTMLScriptElement ? last : null;
}

function mountWidget(script: HTMLScriptElement, config: WidgetConfig): void {
  if (!customElements.get(CUSTOM_ELEMENT_TAG)) {
    customElements.define(CUSTOM_ELEMENT_TAG, SkinnyWidgetElement);
  }

  // When a target id resolves, mount the host INSIDE that element. The
  // serialized `config.targetMode` then decides the layout downstream in
  // `SkinnyWidgetApp` (panel = inline #117, button = Advanced anchored
  // launcher + body-level slide-over panel). When the target is missing we
  // strip `targetId` so the app renders the floating launcher fallback.
  const mountDecision = resolveMountTarget(config.targetId, document);
  const effectiveConfig: WidgetConfig =
    mountDecision.kind === "inline" ? config : { ...config, targetId: undefined };

  const host = document.createElement(CUSTOM_ELEMENT_TAG);
  host.setAttribute(WIDGET_CONFIG_ATTR, JSON.stringify(effectiveConfig));

  if (mountDecision.kind === "inline") {
    // Idempotent remount (Test chat client nav, Show Greeting remount, etc.).
    mountDecision.element.querySelectorAll(CUSTOM_ELEMENT_TAG).forEach((el) => {
      el.remove();
    });
    mountDecision.element.appendChild(host);
  } else {
    if (mountDecision.kind === "missing") {
      console.warn(
        `[Scale2Rev Engage Widget] target_id "${mountDecision.targetId}" was configured but no matching element exists on this page. Falling back to the floating launcher.`,
      );
    }
    document.body.appendChild(host);
  }
}

async function bootstrapWidget(): Promise<void> {
  const script = getBootstrapScript();
  if (!script) {
    console.error(
      "[Scale2Rev Engage Widget] document.currentScript was null. Load the widget via a classic script tag (avoid defer / type=module) so configuration can be read from the tag.",
    );
    return;
  }

  const embed = parseWidgetConfigFromDataset(script);
  const scriptOrigin = new URL(script.src).origin;
  const apiOrigin = embed.apiOrigin ?? scriptOrigin;

  const remoteTheme = await fetchWidgetTheme(apiOrigin, embed.siteId);
  const themedEmbed = mergeEmbedWithRemoteTheme(script, embed, remoteTheme);

  const config = applyResponsivePositionToConfig(
    widgetConfigSchema.parse({
      ...themedEmbed,
      apiOrigin,
      positionDesktop: themedEmbed.positionDesktop,
      positionMobile: themedEmbed.positionMobile,
      positionTablet: themedEmbed.positionTablet,
      ttsEndpoint:
        themedEmbed.ttsEndpoint ?? `${apiOrigin.replace(/\/$/, "")}/api/engage/speech`,
    }),
  );

  mountWidget(script, config);
}

if (typeof document !== "undefined") {
  const kickoff = (): void => {
    if (!document.body) {
      document.addEventListener("DOMContentLoaded", kickoff, { once: true });
      return;
    }
    void bootstrapWidget();
  };
  kickoff();
}
